import DetailsView from "@/components/resume/detail/DetailsView";
import SkillsView from "@/components/resume/skill/SkillsView";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DetailData, EmploymentResponse, SkillData } from '@/lib/api';
import { formatProfileMonth, monthInputToApiDate } from '@/lib/profile-dates';
import { Pencil, Save, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import MonthYearSelect from '../MonthYearSelect';

interface EmploymentViewProps {
  employment: EmploymentResponse;
  details?: DetailData[];
  skills?: SkillData[];
  onDelete?: (id?: string) => void;
  onUpdate?: (employment: EmploymentResponse) => Promise<void> | void;
  onDetailsChange?: (details: DetailData[]) => void;
  loading?: boolean;
}

const EmploymentView: React.FC<EmploymentViewProps> = ({ employment, details, skills, onDelete, onUpdate, onDetailsChange, loading }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftEmployment, setDraftEmployment] = useState<EmploymentResponse>(employment);

  useEffect(() => {
    if (!isEditing) setDraftEmployment(employment);
  }, [employment, isEditing]);

  const startEditing = () => {
    setDraftEmployment(employment);
    setIsEditing(true);
    setActionsOpen(true);
  };

  const cancelEditing = () => {
    setDraftEmployment(employment);
    setIsEditing(false);
    setActionsOpen(false);
  };

  const saveEmployment = async () => {
    await onUpdate?.(draftEmployment);
    setIsEditing(false);
    setActionsOpen(false);
  };

  const updateDraft = (field: keyof EmploymentResponse, value: string | boolean | null) => {
    setDraftEmployment(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="relative border-l-2 border-gray-200 pl-4 sm:pl-6 pb-6 group">
      <div className="absolute w-3 h-3 bg-interview-blue rounded-full -left-[7px] top-[10px]"></div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                value={draftEmployment.title}
                onChange={e => updateDraft('title', e.target.value)}
                placeholder="Job title"
                disabled={loading}
              />
              <Input
                value={draftEmployment.company}
                onChange={e => updateDraft('company', e.target.value)}
                placeholder="Company"
                disabled={loading}
              />
              <MonthYearSelect
                value={draftEmployment.start_date}
                onChange={value => updateDraft('start_date', monthInputToApiDate(value))}
                disabled={loading}
              />
              <MonthYearSelect
                value={draftEmployment.end_date}
                onChange={value => updateDraft('end_date', monthInputToApiDate(value) || null)}
                disabled={loading || draftEmployment.is_ongoing}
              />
              <label className="flex items-center gap-2 text-sm text-gray-600 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(draftEmployment.is_ongoing)}
                  onChange={e => {
                    updateDraft('is_ongoing', e.target.checked);
                    if (e.target.checked) updateDraft('end_date', null);
                  }}
                  disabled={loading}
                  className="h-4 w-4 rounded border-gray-300 text-interview-blue focus:ring-interview-blue"
                />
                Currently working here
              </label>
            </div>
          ) : (
            <>
              <h4 className="text-base sm:text-lg font-semibold text-interview-blue break-words">
                {employment.title}
              </h4>
              <h5 className="text-sm sm:text-base font-medium mt-1 break-words">{employment.company || 'Unknown Company'}</h5>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-600 sm:whitespace-nowrap">
            {formatProfileMonth(employment.start_date)}{formatProfileMonth(employment.start_date) && ' - '}{employment.is_ongoing ? 'Present' : formatProfileMonth(employment.end_date)}
            {employment.is_ongoing && (
              <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200 text-xs">
                Current
              </Badge>
            )}
          </div>
          {onDelete && (
            <div className="flex items-center gap-1">
              {actionsOpen ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={saveEmployment}
                    disabled={loading || !draftEmployment.title || !draftEmployment.company || !draftEmployment.start_date}
                    aria-label="Save employment"
                  >
                    <Save className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={cancelEditing}
                    disabled={loading}
                    aria-label="Cancel employment edit"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onDelete(employment.id)}
                    disabled={loading}
                    aria-label="Delete employment"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={startEditing}
                  disabled={loading}
                  aria-label="Edit employment"
                >
                  <Pencil className="h-4 w-4 text-interview-blue" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Details & Achievements */}
      <h5 className="text-sm sm:text-md font-semibold mt-4 mb-2">Details & Achievements</h5>
      <DetailsView employmentId={employment.id} details={details} onDetailsChange={onDetailsChange} />

      {/* Skills */}
      <h5 className="text-sm sm:text-md font-semibold mt-4 mb-2">Skills</h5>
      <SkillsView employmentId={employment.id} skills={skills} />

    </div>
  );
};

export default EmploymentView;
